import { AdminCohorts } from '@platform/client';
import { AdminCohort } from '@platform/client/schemas';
import { keepPreviousData } from '@tanstack/react-query';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useContext, useMemo, useState } from 'react';
import { AdminActionsContainer, AdminListPageWrapper } from '../../AdminListPage';
import { AdminTable, AdminTableContext } from '../../AdminTable';
import { CreateCohortModal } from './CreateCohortModal';
import { EditCohortModal } from './EditCohortModal';

export const CohortsAdmin = () => {
  return (
    <AdminListPageWrapper title='Cohorts'>
      <CohortsAdminContent />
    </AdminListPageWrapper>
  );
};

const columnHelper = createColumnHelper<AdminCohort>();

const CohortsAdminContent = () => {
  const { adminQueryOptionsV2 } = useContext(AdminTableContext);
  const [createCohortIsOpen, setCreateCohortIsOpen] = useState(false);
  const [updateCohortId, setUpdateCohortId] = useState<number>();
  const { data, isLoading, isSuccess, isError } = AdminCohorts.useGetAdminCohorts(
    adminQueryOptionsV2,
    {
      query: { placeholderData: keepPreviousData },
    },
  );
  const { cohorts: cohortsData = [], filteredCount = 0 } = data || {};
  const columns = useMemo(
    () =>
      [
        columnHelper.accessor('id', {
          header: 'ID',
          cell: (id) => id.getValue(),
          meta: {
            filterVariant: 'text',
          },
          size: 25,
        }),
        columnHelper.accessor('name', {
          header: 'Name',
          cell: (name) => name.getValue(),
          meta: {
            filterVariant: 'text',
          },
          size: 250,
        }),
        columnHelper.accessor('client', {
          header: 'Corporate Client',
          cell: (client) => client.getValue() || 'N/A',
          meta: {
            filterVariant: 'text',
          },
          size: 250,
        }),
        columnHelper.accessor('template', {
          header: 'Cohort Template',
          cell: (template) => template.getValue()?.cohortType || 'N/A',
          meta: {
            filterVariant: 'text',
          },
        }),
        columnHelper.accessor('duration', {
          header: 'Duration',
          cell: (duration) => duration.getValue() || 'N/A',
          meta: {
            filterVariant: 'text',
          },
          size: 150,
        }),
        columnHelper.accessor('timeCommitment', {
          header: 'Time Commitment',
          cell: (timeCommitment) => timeCommitment.getValue() || 'N/A',
          meta: {
            filterVariant: 'text',
          },
          size: 150,
        }),
        columnHelper.accessor('applicationStartDate', {
          header: 'Application Start Date',
          cell: (applicationStartDate) => {
            const date = applicationStartDate.getValue();
            if (!date) {
              return 'N/A';
            }
            return new Date(date).toLocaleDateString();
          },
          enableColumnFilter: false,
          size: 150,
        }),
        columnHelper.accessor('applicationEndDate', {
          header: 'Application End Date',
          cell: (applicationEndDate) => {
            const date = applicationEndDate.getValue();
            if (!date) {
              return 'N/A';
            }
            return new Date(date).toLocaleDateString();
          },
          enableColumnFilter: false,
          size: 150,
        }),
        columnHelper.accessor('startDate', {
          header: 'Start Date',
          cell: (startDate) => {
            const date = startDate.getValue();
            if (!date) {
              return 'N/A';
            }
            return new Date(date).toLocaleDateString();
          },
          enableColumnFilter: false,
          size: 150,
        }),
        columnHelper.accessor('kickoffDate', {
          header: 'Kickoff Date',
          cell: (kickoffDate) => {
            const date = kickoffDate.getValue();
            if (!date) {
              return 'N/A';
            }
            return new Date(date).toLocaleDateString();
          },
          enableColumnFilter: false,
          size: 150,
        }),
        columnHelper.accessor('endDate', {
          header: 'End Date',
          cell: (endDate) => {
            const date = endDate.getValue();
            if (!date) {
              return 'N/A';
            }
            return new Date(date).toLocaleDateString();
          },
          enableColumnFilter: false,
          size: 150,
        }),
        columnHelper.accessor('createdAt', {
          header: 'Created At',
          cell: (createdAt) => new Date(createdAt.getValue()).toLocaleDateString(),
          meta: {
            filterVariant: 'date-select',
          },
          size: 150,
        }),
        columnHelper.accessor('updatedAt', {
          header: 'Updated At',
          cell: (updatedAt) => new Date(updatedAt.getValue()).toLocaleDateString(),
          meta: {
            filterVariant: 'date-select',
          },
          size: 150,
        }),
        columnHelper.display({
          id: 'actions',
          header: 'Actions',
          cell: ({ row }) => (
            <AdminActionsContainer
              actions={[
                {
                  type: 'button',
                  text: 'Edit',
                  onPress: () => setUpdateCohortId(row.original.id),
                },
              ]}
            />
          ),
          size: 100,
        }),
      ] as ColumnDef<AdminCohort>[],
    [],
  );
  return (
    <>
      <AdminTable
        columns={columns}
        data={cohortsData}
        filteredCount={filteredCount}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        enableFiltering={true}
        tableActions={[{ text: 'Create Cohort', callback: () => setCreateCohortIsOpen(true) }]}
      />
      {createCohortIsOpen && <CreateCohortModal onClose={() => setCreateCohortIsOpen(false)} />}
      {updateCohortId && (
        <EditCohortModal cohortId={updateCohortId} onClose={() => setUpdateCohortId(undefined)} />
      )}
    </>
  );
};
